<template>
  <div v-once></div>
</template>

<script>
import { SEO_CONFIG } from '@/utils/seo'

export default {
  name: 'SeoMeta',
  props: {
    page: {
      type: String,
      required: true
    }
  },
  computed: {
    currentLocale() {
      return this.$i18n?.locale || document.documentElement.lang || 'ar'
    },
    metaTitle() {
      const config = SEO_CONFIG[this.page.toLowerCase()]
      if (!config?.title) return this.currentLocale === 'ar' ? 'سلاسة | نظام محاسبة وفوترة إلكترونية معتمد' : 'Salasah | Certified Electronic Accounting System'
      
      return typeof config.title === 'object' 
        ? config.title[this.currentLocale] || config.title.ar
        : config.title
    },
    metaDescription() {
      const config = SEO_CONFIG[this.page.toLowerCase()]
      if (!config?.description) return this.currentLocale === 'ar' ? 'سلاسة هو نظام محاسبة وفوترة إلكتروني معتمد من هيئة الزكاة والضريبة والجمارك' : 'Salasah is a certified electronic accounting and invoicing system by ZATCA'
      
      return typeof config.description === 'object'
        ? config.description[this.currentLocale] || config.description.ar  
        : config.description
    },
    metaKeywords() {
      const config = SEO_CONFIG[this.page.toLowerCase()]
      if (!config?.keywords) return this.currentLocale === 'ar' ? 'سلاسة, نظام محاسبة إلكتروني, نظام فوترة إلكتروني' : 'salasah, electronic accounting, e-invoicing'
      
      return typeof config.keywords === 'object'
        ? config.keywords[this.currentLocale] || config.keywords.ar
        : config.keywords
    },
    currentUrl() {
      return window.location.href
    },
    canonicalUrl() {
      return `https://salasah.sa${this.$route.path}`
    },
    alternateUrl() {
      const baseUrl = 'https://salasah.sa'
      const path = this.$route.path
      const otherLang = this.currentLocale === 'ar' ? 'en' : 'ar'
      return `${baseUrl}/${otherLang}${path}`
    }
  },
  mounted() {
    this.updateMetaTags()
  },
  methods: {
    updateMetaTags() {
      // Update document title
      document.title = this.metaTitle
      
      // Basic meta tags
      this.updateMetaTag('description', this.metaDescription)
      this.updateMetaTag('keywords', this.metaKeywords)
      this.updateMetaTag('author', 'سلاسة - Salasah')
      this.updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1')
      this.updateMetaTag('googlebot', 'index, follow')
      
      // Language and direction meta tags
      this.updateMetaTag('content-language', this.currentLocale === 'ar' ? 'ar-SA' : 'en-US')
      document.documentElement.setAttribute('lang', this.currentLocale === 'ar' ? 'ar-SA' : 'en-US')
      document.documentElement.setAttribute('dir', this.currentLocale === 'ar' ? 'rtl' : 'ltr')
      
      // Open Graph meta tags
      this.updateMetaTag('og:title', this.metaTitle)
      this.updateMetaTag('og:description', this.metaDescription)
      this.updateMetaTag('og:type', 'website')
      this.updateMetaTag('og:url', this.currentUrl)
      this.updateMetaTag('og:site_name', this.currentLocale === 'ar' ? 'سلاسة' : 'Salasah')
      this.updateMetaTag('og:locale', this.currentLocale === 'ar' ? 'ar_SA' : 'en_US')
      this.updateMetaTag('og:locale:alternate', this.currentLocale === 'ar' ? 'en_US' : 'ar_SA')
      this.updateMetaTag('og:image', 'https://salasah.sa/assets/og-image.jpg')
      this.updateMetaTag('og:image:width', '1200')
      this.updateMetaTag('og:image:height', '630')
      this.updateMetaTag('og:image:alt', this.metaTitle)
      
      // Twitter Card meta tags
      this.updateMetaTag('twitter:card', 'summary_large_image')
      this.updateMetaTag('twitter:title', this.metaTitle)
      this.updateMetaTag('twitter:description', this.metaDescription)
      this.updateMetaTag('twitter:image', 'https://salasah.sa/assets/og-image.jpg')
      this.updateMetaTag('twitter:site', '@salasah_sa')
      this.updateMetaTag('twitter:creator', '@salasah_sa')
      
      // Additional SEO meta tags
      this.updateMetaTag('theme-color', '#0f4640')
      this.updateMetaTag('msapplication-TileColor', '#0f4640')
      this.updateMetaTag('application-name', this.currentLocale === 'ar' ? 'سلاسة' : 'Salasah')
      this.updateMetaTag('apple-mobile-web-app-title', this.currentLocale === 'ar' ? 'سلاسة' : 'Salasah')
      this.updateMetaTag('apple-mobile-web-app-capable', 'yes')
      this.updateMetaTag('apple-mobile-web-app-status-bar-style', 'default')
      
      // Business/Location specific meta tags for Saudi Arabia
      this.updateMetaTag('geo.region', 'SA-01') // Riyadh region code
      this.updateMetaTag('geo.country', 'SA')
      this.updateMetaTag('geo.placename', this.currentLocale === 'ar' ? 'الرياض' : 'Riyadh')
      this.updateMetaTag('ICBM', '24.7136,46.6753') // Riyadh coordinates
      
      this.updateCanonicalLink()
      this.updateAlternateLinks()
      this.updateStructuredData()
    },
    updateMetaTag(name, content) {
      if (!content) return
      
      let element = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`)
      if (!element) {
        element = document.createElement('meta')
        element.setAttribute(name.startsWith('og:') || name.startsWith('twitter:') ? 'property' : 'name', name)
        document.head.appendChild(element)
      }
      element.setAttribute('content', content)
    },
    updateCanonicalLink() {
      let canonical = document.querySelector('link[rel="canonical"]')
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.setAttribute('rel', 'canonical')
        document.head.appendChild(canonical)
      }
      canonical.setAttribute('href', this.canonicalUrl)
    },
    updateAlternateLinks() {
      // Remove existing alternate links
      const existingLinks = document.querySelectorAll('link[rel="alternate"][hreflang]')
      existingLinks.forEach(link => link.remove())
      
      // Add Arabic alternate link
      const arLink = document.createElement('link')
      arLink.setAttribute('rel', 'alternate')
      arLink.setAttribute('hreflang', 'ar-SA')
      arLink.setAttribute('href', `https://salasah.sa${this.$route.path}`)
      document.head.appendChild(arLink)
      
      // Add English alternate link
      const enLink = document.createElement('link')
      enLink.setAttribute('rel', 'alternate')
      enLink.setAttribute('hreflang', 'en-US')
      enLink.setAttribute('href', `https://salasah.sa/en${this.$route.path}`)
      document.head.appendChild(enLink)
      
      // Add x-default link
      const defaultLink = document.createElement('link')
      defaultLink.setAttribute('rel', 'alternate')
      defaultLink.setAttribute('hreflang', 'x-default')
      defaultLink.setAttribute('href', this.canonicalUrl)
      document.head.appendChild(defaultLink)
    },
    updateStructuredData() {
      // Remove existing structured data
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]')
      existingScripts.forEach(script => script.remove())
      
      // Create comprehensive structured data
      const structuredData = this.getStructuredData()
      
      const script = document.createElement('script')
      script.setAttribute('type', 'application/ld+json')
      script.textContent = JSON.stringify(structuredData)
      document.head.appendChild(script)
    },
    getStructuredData() {
      const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": "https://salasah.sa/#organization",
        "name": this.currentLocale === 'ar' ? 'سلاسة' : 'Salasah',
        "legalName": this.currentLocale === 'ar' ? 'شركة سلاسة للحلول الرقمية' : 'Salasah Digital Solutions Company',
        "url": "https://salasah.sa/",
        "logo": {
          "@type": "ImageObject",
          "url": "https://salasah.sa/assets/logo.png",
          "width": 200,
          "height": 60
        },
        "description": this.currentLocale === 'ar' 
          ? 'شركة سلاسة هي شركة رائدة في الحلول الرقمية وأنظمة إدارة الأعمال في المملكة العربية السعودية. نقدم أنظمة محاسبية وفوترة إلكترونية معتمدة من هيئة الزكاة والضريبة والجمارك.'
          : 'Salasah is a leading digital solutions and business management systems company in Saudi Arabia. We provide accounting and electronic invoicing systems certified by ZATCA.',
        "foundingDate": "2020",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "SA",
          "addressRegion": this.currentLocale === 'ar' ? 'منطقة الرياض' : 'Riyadh Region',
          "addressLocality": this.currentLocale === 'ar' ? 'الرياض' : 'Riyadh',
          "streetAddress": this.currentLocale === 'ar' ? 'المملكة العربية السعودية' : 'Saudi Arabia'
        },
        "contactPoint": [
          {
            "@type": "ContactPoint",
            "contactType": this.currentLocale === 'ar' ? 'خدمة العملاء' : 'customer service',
            "telephone": "+966-11-XXXXXXX",
            "email": "info@salasah.sa",
            "availableLanguage": ["Arabic", "English"],
            "areaServed": "SA",
            "hoursAvailable": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": [
                "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"
              ],
              "opens": "08:00",
              "closes": "17:00"
            }
          },
          {
            "@type": "ContactPoint",
            "contactType": this.currentLocale === 'ar' ? 'الدعم الفني' : 'technical support',
            "telephone": "+966-11-XXXXXXX",
            "email": "support@salasah.sa",
            "availableLanguage": ["Arabic", "English"],
            "areaServed": "SA"
          }
        ],
        "sameAs": [
          "https://twitter.com/salasah_sa",
          "https://linkedin.com/company/salasah",
          "https://facebook.com/salasah.sa",
          "https://instagram.com/salasah_sa"
        ],
        "makesOffer": this.getServicesOffered(),
        "areaServed": {
          "@type": "Country",
          "name": this.currentLocale === 'ar' ? 'المملكة العربية السعودية' : 'Saudi Arabia',
          "@id": "https://en.wikipedia.org/wiki/Saudi_Arabia"
        },
        "knowsAbout": [
          this.currentLocale === 'ar' ? 'أنظمة المحاسبة الإلكترونية' : 'Electronic Accounting Systems',
          this.currentLocale === 'ar' ? 'الفوترة الإلكترونية' : 'Electronic Invoicing',
          this.currentLocale === 'ar' ? 'إدارة الموارد البشرية' : 'Human Resources Management',
          this.currentLocale === 'ar' ? 'إدارة علاقات العملاء' : 'Customer Relationship Management',
          this.currentLocale === 'ar' ? 'إدارة المشاريع' : 'Project Management',
          this.currentLocale === 'ar' ? 'أنظمة التصنيع' : 'Manufacturing Systems'
        ]
      }

      const websiteData = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": "https://salasah.sa/#website",
        "url": "https://salasah.sa/",
        "name": this.currentLocale === 'ar' ? 'سلاسة' : 'Salasah',
        "description": this.metaDescription,
        "inLanguage": [
          {
            "@type": "Language",
            "name": "Arabic",
            "alternateName": "ar-SA"
          },
          {
            "@type": "Language", 
            "name": "English",
            "alternateName": "en-US"
          }
        ],
        "publisher": {
          "@id": "https://salasah.sa/#organization"
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://salasah.sa/search?q={search_term_string}"
          },
          "query-input": "required name=search_term_string"
        }
      }

      const webPageData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${this.currentUrl}#webpage`,
        "url": this.currentUrl,
        "name": this.metaTitle,
        "description": this.metaDescription,
        "inLanguage": this.currentLocale === 'ar' ? 'ar-SA' : 'en-US',
        "isPartOf": {
          "@id": "https://salasah.sa/#website"
        },
        "about": {
          "@id": "https://salasah.sa/#organization"
        },
        "datePublished": "2024-01-01T00:00:00+03:00",
        "dateModified": new Date().toISOString(),
        "breadcrumb": this.getBreadcrumbData(),
        "mainEntity": this.getPageMainEntity()
      }

      // Return structured data as an array for multiple entities
      const structuredDataArray = [organizationData, websiteData, webPageData]

      // Add service-specific structured data
      if (['financial', 'hr', 'crm', 'manufacturing', 'projects', 'support'].includes(this.page.toLowerCase())) {
        structuredDataArray.push(this.getServiceStructuredData())
      }

      return {
        "@context": "https://schema.org",
        "@graph": structuredDataArray
      }
    },
    getServicesOffered() {
      const services = {
        ar: [
          {
            "@type": "Service",
            "name": "نظام المحاسبة والفوترة الإلكترونية",
            "description": "نظام محاسبة وفوترة إلكتروني معتمد من هيئة الزكاة والضريبة والجمارك"
          },
          {
            "@type": "Service", 
            "name": "نظام إدارة الموارد البشرية",
            "description": "نظام شامل لإدارة الموظفين والرواتب والحضور والانصراف"
          },
          {
            "@type": "Service",
            "name": "نظام إدارة علاقات العملاء",
            "description": "نظام CRM لإدارة العملاء وزيادة المبيعات"
          },
          {
            "@type": "Service",
            "name": "نظام إدارة المشاريع",
            "description": "أدوات قوية لتخطيط وإدارة ومتابعة المشاريع"
          }
        ],
        en: [
          {
            "@type": "Service",
            "name": "Electronic Accounting and Invoicing System",
            "description": "ZATCA-certified accounting and electronic invoicing system"
          },
          {
            "@type": "Service",
            "name": "Human Resources Management System", 
            "description": "Comprehensive system for employee, payroll, and attendance management"
          },
          {
            "@type": "Service",
            "name": "Customer Relationship Management System",
            "description": "CRM system for customer management and sales growth"
          },
          {
            "@type": "Service",
            "name": "Project Management System",
            "description": "Powerful tools for project planning, management, and tracking"
          }
        ]
      }
      
      return services[this.currentLocale] || services.ar
    },
    getBreadcrumbData() {
      const pathSegments = this.$route.path.split('/').filter(segment => segment)
      const breadcrumbItems = [
        {
          "@type": "ListItem",
          "position": 1,
          "name": this.currentLocale === 'ar' ? 'الرئيسية' : 'Home',
          "item": "https://salasah.sa/"
        }
      ]

      pathSegments.forEach((segment, index) => {
        breadcrumbItems.push({
          "@type": "ListItem",
          "position": index + 2,
          "name": this.getBreadcrumbName(segment),
          "item": `https://salasah.sa/${pathSegments.slice(0, index + 1).join('/')}`
        })
      })

      return {
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbItems
      }
    },
    getBreadcrumbName(segment) {
      const breadcrumbNames = {
        ar: {
          'financial': 'النظام المالي',
          'hr': 'الموارد البشرية', 
          'crm': 'إدارة العملاء',
          'manufacturing': 'التصنيع',
          'projects': 'المشاريع',
          'support': 'الدعم',
          'about': 'حول سلاسة',
          'contact': 'اتصل بنا',
          'services': 'الخدمات'
        },
        en: {
          'financial': 'Financial System',
          'hr': 'Human Resources',
          'crm': 'Customer Management', 
          'manufacturing': 'Manufacturing',
          'projects': 'Projects',
          'support': 'Support',
          'about': 'About',
          'contact': 'Contact',
          'services': 'Services'
        }
      }
      
      return breadcrumbNames[this.currentLocale]?.[segment] || segment
    },
    getPageMainEntity() {
      // Return page-specific main entity based on page type
      const pageEntities = {
        'financial': {
          "@type": "SoftwareApplication",
          "name": this.currentLocale === 'ar' ? 'نظام سلاسة المالي' : 'Salasah Financial System',
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web Browser",
          "offers": {
            "@type": "Offer",
            "priceCurrency": "SAR"
          }
        },
        'hr': {
          "@type": "SoftwareApplication", 
          "name": this.currentLocale === 'ar' ? 'نظام سلاسة للموارد البشرية' : 'Salasah HR System',
          "applicationCategory": "BusinessApplication"
        }
      }
      
      return pageEntities[this.page.toLowerCase()] || null
    },
    getServiceStructuredData() {
      return {
        "@type": "Service",
        "@id": `${this.currentUrl}#service`,
        "name": this.metaTitle,
        "description": this.metaDescription,
        "provider": {
          "@id": "https://salasah.sa/#organization"
        },
        "areaServed": {
          "@type": "Country",
          "name": this.currentLocale === 'ar' ? 'المملكة العربية السعودية' : 'Saudi Arabia'
        },
        "serviceType": this.getServiceType(),
        "category": this.currentLocale === 'ar' ? 'حلول الأعمال الرقمية' : 'Digital Business Solutions',
        "audience": {
          "@type": "Audience",
          "audienceType": this.currentLocale === 'ar' ? 'الشركات والمؤسسات' : 'Companies and Organizations'
        }
      }
    },
    getServiceType() {
      const serviceTypes = {
        ar: {
          financial: 'النظام المالي والمحاسبي',
          hr: 'إدارة الموارد البشرية', 
          crm: 'إدارة علاقات العملاء',
          manufacturing: 'إدارة التصنيع',
          projects: 'إدارة المشاريع',
          support: 'الدعم الفني'
        },
        en: {
          financial: 'Financial & Accounting System',
          hr: 'Human Resources Management',
          crm: 'Customer Relationship Management', 
          manufacturing: 'Manufacturing Management',
          projects: 'Project Management',
          support: 'Technical Support'
        }
      }
      
      return serviceTypes[this.currentLocale]?.[this.page.toLowerCase()] || this.metaTitle
    }
  },
  watch: {
    $route: {
      handler() {
        this.$nextTick(() => {
          this.updateMetaTags()
        })
      },
      immediate: false
    },
    '$i18n.locale': {
      handler() {
        this.$nextTick(() => {
          this.updateMetaTags()
        })
      }
    }
  }
}
</script> 